import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import {
  ChartBarIcon, BuildingOfficeIcon, CogIcon, CreditCardIcon,
  SparklesIcon, CpuChipIcon, CurrencyDollarIcon, TagIcon,
  BriefcaseIcon, NewspaperIcon, DocumentTextIcon, HeartIcon,
  ChevronLeftIcon, Bars3Icon, ArrowRightOnRectangleIcon, HomeIcon
} from '@heroicons/react/24/outline';
import logo from '../../assets/images/icons/Logo_orange.png';

const AdminLayout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  
  // Состояние для данных пользователя
  const [user, setUser] = useState({
    name: 'Загрузка...',
    role: 'Admin'
  });

  // Закрытие меню профиля при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Имитация получения данных аккаунта
  useEffect(() => {
    const timer = setTimeout(() => {
      setUser({
        name: 'Tahyr Orazdurdyyev',
        role: 'Super Admin'
      });
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Генерация инициалов (Tahyr Oraz -> TO)
  const getInitials = (name) => {
    if (!name || name === 'Загрузка...') return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    // Здесь будет логика удаления токена (localStorage.removeItem('token') и т.д.)
    ("Выход из системы...");
    navigate('/login');
  };

  const sidebarItems = [
    { name: 'Analytics', href: '/admin/analytics', icon: ChartBarIcon },
    { name: 'Companies', href: '/admin/companies', icon: BuildingOfficeIcon },
    { name: 'Service Categories', href: '/admin/service-categories', icon: TagIcon },
    { name: 'Business Types', href: '/admin/business-types', icon: BuildingOfficeIcon },
    { name: 'Pet Types', href: '/admin/pet-types', icon: HeartIcon },
    { name: 'Currencies', href: '/admin/currencies', icon: CurrencyDollarIcon },
    { name: 'Plan Settings', href: '/admin/plan-settings', icon: CogIcon },
    { name: 'Payment Settings', href: '/admin/payment-settings', icon: CreditCardIcon },
    { name: 'AI Agents', href: '/admin/ai-agents', icon: CpuChipIcon },
    { name: 'AI Prompts', href: '/admin/prompts', icon: SparklesIcon },
    { name: 'Careers', href: '/admin/careers', icon: BriefcaseIcon },
    { name: 'Press Center', href: '/admin/press', icon: NewspaperIcon },
    { name: 'Blog', href: '/admin/blog', icon: DocumentTextIcon }
  ];

  return (
    <div className="theme-admin flex h-screen">
      <aside 
        className={`${isCollapsed ? 'w-20' : 'w-72'} bg-white border-r border-gray-100 flex flex-col shrink-0 transition-all duration-300 relative`} 
      >
        {/* Header Section */}
        <div className={`h-24 flex items-center shrink-0 ${isCollapsed ? 'justify-center' : 'justify-between px-8'}`}>
          {!isCollapsed && (
            <div className="animate-in fade-in duration-300">
              <Link to="/" className="group">
                <img 
                  src={logo} 
                  alt="Zootel" 
                  className="h-8 w-auto transition-transform group-hover:scale-105" 
                />
              </Link>
            </div>
          )}
          <button 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`flex items-center justify-center hover:bg-slate-50 rounded-xl text-gray-400 hover:text-slate-900 transition-all ${isCollapsed ? 'w-12 h-12' : 'p-2'}`}
          >
            {isCollapsed ? <Bars3Icon className="h-8 w-8 stroke-[2.2]" /> : <ChevronLeftIcon className="h-5 w-5 stroke-[2.5]" />}
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 space-y-1.5 no-scrollbar">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center rounded-2xl transition-all duration-200 group ${
                  isCollapsed ? 'justify-center h-12 w-12 mx-auto' : 'px-4 py-3.5'
                } ${isActive ? 'bg-gray-900 text-white shadow-lg shadow-gray-200' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-900'}`}
              >
                <Icon className={`h-5 w-5 shrink-0 ${isCollapsed ? '' : 'mr-4'} ${isActive ? 'text-white stroke-[2.5]' : 'group-hover:text-slate-900 stroke-[2]'}`} />
                {!isCollapsed && (
                  <span className="text-[11px] font-black uppercase tracking-[0.12em] truncate whitespace-nowrap animate-in fade-in duration-300">
                    {item.name}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer Profile Section */}
        <div className="relative" ref={profileRef}>
          {/* Dropdown Menu */}
          {isProfileOpen && (
            <div className={`absolute bottom-[85%] bg-white border border-gray-100 rounded-2xl shadow-2xl shadow-slate-200 p-2 z-50 animate-in slide-in-from-bottom-2 duration-200 ${isCollapsed ? 'left-2 w-48' : 'left-6 right-6'}`}>
              <button 
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                <span className="text-[10px] font-black uppercase italic">Выйти</span>
              </button>
            </div>
          )}

          {/* Profile Trigger */}
          <div 
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className={`p-6 border-t border-gray-50 flex shrink-0 cursor-pointer hover:bg-slate-50 transition-colors ${isCollapsed ? 'justify-center' : 'px-8'}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white font-black text-[10px] shrink-0 border-2 border-white shadow-md italic uppercase">
                {getInitials(user.name)}
              </div>
              {!isCollapsed && (
                <div className="overflow-hidden animate-in fade-in duration-300">
                  <p className="text-[10px] font-black text-gray-900 uppercase italic leading-none truncate">{user.name}</p>
                  <p className="text-[8px] text-green-500 font-bold uppercase tracking-widest mt-1">{user.role}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-20 bg-white border-b border-gray-100 flex items-center px-10 justify-between">
          <h1 className="text-[11px] font-black text-gray-900 uppercase tracking-[0.2em] italic">
            {sidebarItems.find(i => i.href === pathname)?.name || 'Dashboard'}
          </h1>
        </header>
        <div className="flex-1 overflow-auto p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
